import { useState, useEffect, useCallback } from "react";
import constants from "../../../utils/constants";

// Components
import FormBoxIndex from "./Childs/FormBoxIndex";
import Selector from "./Childs/Selector";
import Form from "./Childs/Form";
import FormCard from "../../Common/Childs/Structure/FormCard";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import Delete from "./Delete";

// Context
import { useWallet } from "../../../context/WalletContextProvider";
import { useGlobal } from "../../../context/GlobalContextProvider";

// Assets
import plus from "../../../assets/icons/plus-solid.svg";

// Constants
const INITIAL_FORM_STATE = {
  id: 0,
  art_pack_name: "",
  artist_id: 0,
  copyright_notice_url: "https://github.com/a16z/a16z-contracts/blob/master/licenses/pdf/05%20-%20a16z%20CBE%20Form%20License%20(CBE-Personal-No-Hate).pdf",
  preview_url: "",
  preview_thumbnail_url: "",
  available_for_campaigns: false,
  min_artist_percentage: 0
};

const DELETE_ERROR_MESSAGE = 
  "This Art Pack cannot be deleted. It may contain information used in Background and Pattern, or is being used in a current campaign.";

// API Service
const artPackAPI = {
  getAll: (account) => 
    fetch(`/api/artist/${account}/artpack`).then(res => {
      if (res.status === 204 || !res.ok) return [];
      return res.json();
    }),
  
  create: (account, data) =>
    fetch(`/api/artist/${account}/artpack`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  
  update: (account, id, data) =>
    fetch(`/api/artist/${account}/artpack/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  
  delete: (account, id) =>
    fetch(`/api/artist/${account}/artpack/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }).then(res => {
      if (res.status === 204 || !res.ok) return null;
      return res.json();
    }),
};


const Index = () => {
  const { account } = useWallet();
  const { setSpinner, artPack, setArtPack } = useGlobal();

  const [artPacks, setArtPacks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  // Load art packs from API
  const loadArtPacks = useCallback(async () => {
    if (!account) return;
    
    setSpinner(true);
    try {
      const data = await artPackAPI.getAll(account);
      setArtPacks(data);
    } catch (error) {
      console.error("Error loading art packs:", error);
    } finally {
      setSpinner(false);
    }
  }, [account, setSpinner]);

  // Load data when account changes
  useEffect(() => {
    loadArtPacks();
  }, [loadArtPacks]);

  // Reset form and close
  const resetForm = useCallback(() => {
    setShowForm(false);
    setIsDeleteMode(false);
    setFormData(INITIAL_FORM_STATE);
    setArtPack(null);
  }, []);

  // Open form for new art pack
  const handleAddNew = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open form for editing
  const handleEdit = useCallback((id) => {
    const artPackToEdit = artPacks.find(item => Number(item.art_pack_id) === Number(id));
    if (artPackToEdit) {
      setFormData({ ...artPackToEdit, id });
      setShowForm(true);
      setIsDeleteMode(false);
    }
  }, [artPacks]);

  // Open delete confirmation
  const handleDelete = useCallback((id) => {
    const artPackToDelete = artPacks.find(item => Number(item.art_pack_id) === Number(id));
    if (artPackToDelete) {
      setFormData({ ...artPackToDelete, id });
      setShowForm(false);
      setIsDeleteMode(true);
    }
  }, [artPacks]);

  // Clear selected art pack from session
  const clearSelectedArtPack = useCallback(() => {
    sessionStorage.removeItem(constants.ART_PACK_IDENTIFIER);
    setArtPack(null);
  }, [setArtPack]);

  // Create new art pack
  const handleCreate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await artPackAPI.create(account, data);
      await loadArtPacks();
      resetForm();
    } catch (error) {
      console.error("Error creating art pack:", error);
    } finally {
      setSpinner(false);
    }
  }, [account, loadArtPacks, resetForm, setSpinner]);

  // Update existing art pack
  const handleUpdate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await artPackAPI.update(account, formData.id, data);
      await loadArtPacks();
      resetForm();
    } catch (error) {
      console.error("Error updating art pack:", error);
    } finally {
      setSpinner(false);
    }
  }, [account, formData.id, loadArtPacks, resetForm, setSpinner]);

  // Delete art pack
  const handleDeleteConfirm = useCallback(async () => {
    setSpinner(true);
    try {
      const result = await artPackAPI.delete(account, formData.id);
      
      if (result.availableToDelete) {
        if (result.id === artPack?.art_pack_id) {
          clearSelectedArtPack();
        }
        await loadArtPacks();
        resetForm();
      } else {
        alert(DELETE_ERROR_MESSAGE);
      }
    } catch (error) {
      console.error("Error deleting art pack:", error);
    } finally {
      setSpinner(false);
    }
  }, [account, formData.id, artPack, clearSelectedArtPack, loadArtPacks, resetForm, setSpinner]);

  // Render delete confirmation
  if (isDeleteMode && formData.id) {
    return (
      <Delete
        onDiscard={resetForm}
        handleSubmit={handleDeleteConfirm}
        formData={formData}
      />
    );
  }

  // Render form
  if (showForm) {
    const isEditMode = Boolean(formData.id);
    
    return (
      <>
        <Title title="Art Pack" />
        <Description text="Set up the basics of your Art Pack here." />
        <FormCard>
          <Form
            handleSubmit={isEditMode ? handleUpdate : handleCreate}
            formData={formData}
            onDiscard={resetForm}
            typeCheck={isEditMode ? 'updateItem' : 'createItem'}
          />
        </FormCard>
      </>
    );
  }

  // Render main list view
  return (
    <>
      <Title title="Art Pack" />
      <Description text="Set up the basics of your Art Pack here." />
      <div className="add-btn" onClick={handleAddNew}>
        Add Art Pack
        <img src={plus} className="add-icon" alt="Add" />
      </div>
      <FormBoxIndex>
        <Selector
          data={artPacks}
          onEdit={handleEdit}
          onDelete={handleDelete}
          artPack={artPack}
          setArtPack={setArtPack}
          isAllowedToEditOrDelete={!artPack?.approved}
        />
      </FormBoxIndex>
    </>
  );
};

export default Index;
